import { CustomObjectChildCard } from "components/ObjectTemplateCanvas/CustomObjectChildCard";
import { CanvasNodeProps } from "components/ObjectTemplateCanvas/ObjectTemplateNode";
import { FormField } from "config/config";
import { Direction } from "containers/Resources/screens/ResourcesSingle/components/Visualization/visualization.service";
import { Custom_Object } from "graphql/generated/graphql";
import { memo, useMemo } from "react";
import { Handle, Position, useReactFlow, useStore } from "reactflow";
import "reactflow/dist/style.css";

export interface CustomObjectChildNodeProps {
    value: Custom_Object;
    layoutDirection: Direction;
    fetchCustomObjectList: () => Promise<any>;
    formStructure?: FormField[];
    setLoading: (isLoading: boolean) => void;
    expandedCustomObjectId: string | null;
    setExpandedCustomObjectId: (id: string | null) => void;
    embedMode?: boolean;
    // Set by the deep-link focus decorator (canvas displayNodes memo) so the card
    // can render the focus/selection ring on its own element.
    isFocused?: boolean;
}

export interface CustomObjectNodeProps {
    id: string;
    data: CustomObjectChildNodeProps;
    isConnectable: boolean;
}

export const CustomObjectChildNode = memo(
    ({ id, data, isConnectable }: CanvasNodeProps<CustomObjectChildNodeProps>) => {
        const { getNode } = useReactFlow();

        const [vx, vy, zoom] = useStore((state) => state.transform);

        const node = getNode(id) as any;
        const nodeWidth = node?.measured?.width ?? node?.width ?? 0;
        const nodeHeight = node?.measured?.height ?? node?.height ?? 0;

        const isInView = useMemo(() => {
            if (data.embedMode) {
                return true;
            }

            if (!node) return true;

            const width = nodeWidth;
            const height = nodeHeight;

            // Child nodes use parent-relative coordinates (extent: "parent"), so
            // their absolute canvas position is positionAbsolute (the field
            // FloatingEdges also relies on), falling back to the parent
            // container's position plus the child's relative offset. Comparing
            // the relative offset directly against the absolute viewport wrongly
            // culled every container that is not at x≈0 — leaving those cards
            // unrendered. Be permissive (render) if geometry is unknown.
            let x: number;
            let y: number;
            if (node.positionAbsolute) {
                x = node.positionAbsolute.x;
                y = node.positionAbsolute.y;
            } else {
                const parentNode = node.parentId ? (getNode(node.parentId) as any) : undefined;
                if (node.parentId && !parentNode) {
                    return true;
                }
                x = (parentNode?.position?.x ?? 0) + node.position.x;
                y = (parentNode?.position?.y ?? 0) + node.position.y;
            }

            const viewX = -vx / zoom;
            const viewY = -vy / zoom;
            const viewWidth = window.innerWidth / zoom;
            const viewHeight = window.innerHeight / zoom;

            return (
                x + width >= viewX &&
                x <= viewX + viewWidth &&
                y + height >= viewY &&
                y <= viewY + viewHeight
            );
        }, [data.embedMode, vx, vy, zoom, node, nodeWidth, nodeHeight, getNode]);

        // NOTE: do NOT unmount the whole node when it scrolls out of view. The
        // <Handle> elements below carry the react-flow handleBounds that
        // FloatingEdges anchors to; if they unmount, react-flow drops every edge
        // touching this node — which made edges vanish as you zoomed in (the
        // in-view viewport shrinks in canvas coords, culling more children).
        // Instead we keep the handles (and a correctly-sized placeholder box, so
        // the handle offsets stay anchored to the real node box) mounted always,
        // and cull only the heavy CustomObjectChildCard body when off-screen.
        return (
            <div className="nowheel">
                {isInView ? (
                    <CustomObjectChildCard
                        data={data.value}
                        fetchCustomObjectList={data.fetchCustomObjectList}
                        formStructure={data.formStructure}
                        setLoading={data.setLoading}
                        expandedId={data.expandedCustomObjectId}
                        setExpandedId={data.setExpandedCustomObjectId}
                        isFocused={data.isFocused}
                    />
                ) : (
                    <div
                        aria-hidden
                        style={{
                            width: nodeWidth || undefined,
                            height: nodeHeight || undefined,
                        }}
                    />
                )}
                {/* Floating edges require handles on all positions (top, bottom, left, right)
                    We use both source and target types to avoid ReactFlow errors.
                    The node box is the visible card, so the default handle positions
                    sit on the card border.
                */}
                <Handle
                    type="source"
                    position={Position.Top}
                    id="source-1"
                    isConnectable={isConnectable}
                />
                <Handle
                    type="target"
                    position={Position.Top}
                    id="target-1"
                    isConnectable={isConnectable}
                />

                <Handle
                    type="source"
                    position={Position.Right}
                    id="source-2"
                    isConnectable={isConnectable}
                />
                <Handle
                    type="target"
                    position={Position.Right}
                    id="target-2"
                    isConnectable={isConnectable}
                />

                <Handle
                    type="source"
                    position={Position.Bottom}
                    id="source-3"
                    isConnectable={isConnectable}
                />
                <Handle
                    type="target"
                    position={Position.Bottom}
                    id="target-3"
                    isConnectable={isConnectable}
                />

                <Handle
                    type="source"
                    position={Position.Left}
                    id="source-4"
                    isConnectable={isConnectable}
                />
                <Handle
                    type="target"
                    position={Position.Left}
                    id="target-4"
                    isConnectable={isConnectable}
                />
            </div>
        );
    }
);
